import axios from 'axios';
import OpenAI from 'openai';
import type { ChatCompletionMessageParam } from 'openai/resources/index.mjs';
import { useState } from 'react';

type useChatStreamProps = {
  model: string;
  apiKey: string;
  endpoint: string;
  onError: (error: any) => void;
  onSuccess: (data: any) => void;
  options?: Omit<OpenAI.Chat.Completions.ChatCompletionCreateParamsStreaming, 'model' | 'messages' | 'stream'>;
  systemPrompt?: string;
};

export const useChatStream = (props: useChatStreamProps) => {
  const [messages, setMessages] = useState<ChatCompletionMessageParam[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const { apiKey, endpoint, model, onError, onSuccess, options, systemPrompt } = props;

  // ฟังก์ชัน handleSubmit สำหรับส่งข้อความและ stream response
  const handleSubmit = async (
    msgs: ChatCompletionMessageParam[]
  ) => {
    try {
      setIsStreaming(true);
      const client = new OpenAI({
        apiKey: apiKey,
        baseURL: endpoint,
        dangerouslyAllowBrowser: true, // สำหรับใช้งานใน browser
      });
      let streamedMessage = '';
      // ถ้ามี systemPrompt ให้ prepend เข้าไปเป็น message แรก
      let finalMessages: ChatCompletionMessageParam[] = msgs;
      if (systemPrompt) {
        if (!msgs[0] || msgs[0].role !== 'system' || msgs[0].content !== systemPrompt) {
          finalMessages = [
            { role: 'system', content: systemPrompt } as ChatCompletionMessageParam,
            ...msgs,
          ];
        }
      }
      const response = await client.chat.completions.create({
        model: model,
        messages: finalMessages,
        stream: true,
        ...options,
      });
      // เตรียม baseMessages สำหรับ stream (finalMessages)
      const baseMessages = [...finalMessages];
      for await (const chunk of response) {
        const content = chunk.choices?.[0]?.delta?.content;
        if (content) {
          streamedMessage += content;
          setMessages([
            ...baseMessages,
            { role: 'assistant', content: streamedMessage } as ChatCompletionMessageParam,
          ]);
        }
      }
      onSuccess(streamedMessage);
    } catch (error) {
      onError(error);
    } finally {
      setIsStreaming(false);
    }
  };

  // ปรับ append ให้เรียก handleSubmit(newMessages) หลัง setMessages
  const append = (message: ChatCompletionMessageParam) => {
    setMessages((prev) => {
      const newMessages = [...prev, message];
      handleSubmit(newMessages);
      return newMessages;
    });
  };

  // ฟังก์ชันสำหรับลบข้อความทั้งหมด
  const reset = () => {
    setMessages([]);
  };

  return {
    messages,
    setMessages,
    append,
    handleSubmit,
    isStreaming,
    reset,
  };
};
